import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, watch, PropType, nextTick } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import {
  TimeViewService,
  RequestParams as LogRequestParams,
  getPeripheralTypeLabel,
} from '@/views/dashboard/data-safe/usb-manage/usb-statistics/_components/time-view/time-view.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  FormType,
  ObjectFormItem,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { formatTimeStamp } from '@/utils/time'
import { FormSize } from '@/components/cmForm/types'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import PolicySelect from '@/views/dashboard/_components/policy-select/policy-select.vue'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import { listCol, listColPair } from '@/utils/list-columns-i18n'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'

type FilterFormKeys = Extract<keyof LogRequestParams, 'strategy_id' | 'peripheral_status' | 'time_range'>


export default /*#__PURE__*/_defineComponent({
  __name: 'usb-access-event-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  hasPermission: {
    type: Boolean,
    default: true
  },
  /** 全量抽屉打开时预选的触发策略（如从策略列表「触发次数」进入） */
  initialStrategyId: {
    type: String,
    default: '',
  },
  /** 全量抽屉打开时预选的记录时间：1 全部 2 近24h 3 近7天 4 近一个月（与 time-view 接口一致） */
  initialTimeRange: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
},
  emits: ['viewAll'],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props

const emits = __emit
const { t } = useI18n()

const injector = useReflectiveInjector([TimeViewService])
const timeViewService = injector.get(TimeViewService)
const dataApi = timeViewService.getPeripheralcontrolViewTimeGet.bind(timeViewService)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

function resolveInitialTimeRange(): number | undefined {
  const v = props.initialTimeRange
  if (v === undefined || v === null) return undefined
  return Number(v)
}

const queryParams = reactive<Partial<LogRequestParams>>({
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  search_word: '',
  sort_key: 'access_time',
  asc: false,
  peripheral_status: 3,
  time_range: resolveInitialTimeRange(),
  strategy_id: props.initialStrategyId || undefined,
})

const filterFormColumns = computed<ObjectFormItem<LogRequestParams, FilterFormKeys>>(() => ({
    strategy_id: {
      prop: 'strategy_id' as const,
      slot: true,
      label: t('usbPolicy.triggerPolicy'),
      type: FormType.Select,
      outSearchFiledFlag: true,
      colOpt: { style: 'flex: 0 0 160px' },
      events: {
        change: () => { void refreshList() },
      },
    },
    peripheral_status: {
      prop: 'peripheral_status' as const,
      label: t('usbPolicy.deviceStatus'),
      type: FormType.Select,
      outSearchFiledFlag: true, colOpt: { style: 'flex: 0 0 160px' },
      dictData: [
        { label: t('usbPolicy.all'), value: 3 },
        { label: t('usbPolicy.enabled'), value: 1 },
        { label: t('usbPolicy.disabled'), value: 2 },
      ],
      events: {
        change: () => { void refreshList() },
      },
    },
    time_range: {
      prop: 'time_range' as const,
      label: t('usbPolicy.recordTime'), colOpt: { style: 'flex: 0 0 160px' },
      type: FormType.Select,
      outSearchFiledFlag: true,
      dictData: [
        { label: t('usbPolicy.all'), value: 1 },
        { label: t('usbPolicy.last24h'), value: 2 },
        { label: t('usbPolicy.last7d'), value: 3 },
        { label: t('usbPolicy.lastMonth'), value: 4 },
      ],
      events: {
        change: () => { void refreshList() },
      },
    },
}))

function onStrategyChange() {
  void refreshList()
}

watch(() => props.initialStrategyId, (id) => {
  queryParams.strategy_id = id || undefined
  if (props.viewMode !== TableViewMode.preview) {
    void refreshList()
  }
})

watch(() => props.initialTimeRange, () => {
  queryParams.time_range = resolveInitialTimeRange()
  if (props.viewMode !== TableViewMode.preview) {
    void refreshList()
  }
})

const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => ({
  columnOpt: { label: listCol(t, 'operation'), width: 120, align: 'left', fixed: 'right' },
  actionButtons: (rowItem: any) => [
    {
      name: listCol(t, 'dataSource'),
      type: 'rawData',
      opt: { size: FormSize.Small, text: true, type: 'text' },
    },
  ],
}))

const tableOpt = computed<TableOpt>(() => ({
  height: props.viewMode === TableViewMode.preview ? undefined : '100%',
  path: 'list',
  showActionBar: false,
  showForm: false,
  showRightToolbar: false,
  rowKey: 'peripheral_id',
  selection: false,
  bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : 'padding:0',
  showTip: false,
  usePagination: false,
  disableScrollLoad: props.viewMode === TableViewMode.preview,
  scrollLoadThreshold: 10,
  scrollLoadDelay: 100,
  columns: [
    { label: listCol(t, 'accessTime'), prop: 'access_time', columnOpt: { minWidth: 160 }, formatter: (_row: any, value: number) => (value ? formatTimeStamp(Number(value), 'YYYY-MM-DD HH:mm') : '--') },
    { label: listCol(t, 'deviceName'), prop: 'peripheral_name',showOverflow:true, columnOpt: { minWidth: 150 } },
    { label: listCol(t, 'triggerPolicy'), prop: 'strategy_name', columnOpt: { minWidth: 120 }, formatter: (_row: any, val: any) => val || '--', showOverflow: true },
    { label: listCol(t, 'deviceType'), prop: 'peripheral_type', columnOpt: { width: 100 } },
    { label: listCol(t, 'deviceId'), prop: 'peripheral_id', columnOpt: { minWidth: 150 }, showOverflow: true },
    { label: t('usbPolicy.deviceStatus'), prop: 'peripheral_status', columnOpt: { width: 100 }, },
    ...computerInfoCols(true,false,t,{mac:false})
  ],
}))

async function handleSearch(value: string) {
  await nextTick()
  queryParams.search_word = value
  await refreshList()
}

async function handleQuery(form: any) {
  Object.assign(queryParams, form)
  await refreshList()
}

function handleReset() {
  queryParams.search_word = ''
  queryParams.peripheral_status = 3
  queryParams.time_range = resolveInitialTimeRange()
  queryParams.strategy_id = props.initialStrategyId || undefined
  void refreshList()
}

function handleAction({ type, rowItem }: { type: any; rowItem?: any }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

function download(type: 0 | 1) {
  const params: Partial<LogRequestParams> = {
    search_word: queryParams.search_word,
    peripheral_status: queryParams.peripheral_status,
    time_range: queryParams.time_range,
    sort_key: queryParams.sort_key,
    asc: queryParams.asc,
    strategy_id: queryParams.strategy_id,
  }
  timeViewService.download(type, params, t('usbPolicy.exportFileName'))
}

/** 首屏仅 CmTable 默认首载；`initialStrategyId` / `initialTimeRange` 仍由 watch 同步后 refresh */
__expose({
  tableRef,
  getList: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      "has-permission": props.hasPermission,
      api: _unref(dataApi),
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode !== _unref(TableViewMode).preview)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !props.hasPermission,
              "show-search": true,
              "search-placeholder": _ctx.$t('usbPolicy.searchPlaceholder'),
              "popover-threshold": 2,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              strategy_id: _withCtx(() => [
                _createVNode(PolicySelect, {
                  modelValue: queryParams.strategy_id,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.strategy_id) = $event)),
                  size: "small",
                  class: "policy-select",
                  clearable: "",
                  disabled: !props.hasPermission,
                  "strategy-type": _unref(PolicyType).usbControl,
                  onChange: onStrategyChange
                }, null, 8, ["modelValue", "disabled", "strategy-type"])
              ]),
              actions: _withCtx(() => [
                _createVNode(ExportDropdown, {
                  "has-permission": props.hasPermission,
                  "csv-i18n-key": "usbPolicy.exportCsv",
                  "excel-i18n-key": "usbPolicy.exportExcel",
                  onExport: download
                }, null, 8, ["has-permission"])
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      peripheral_type: _withCtx(({ value }: { value?: number }) => [
        _createElementVNode("span", null, _toDisplayString(value != null ? (_unref(getPeripheralTypeLabel)(value, _unref(t)) || '--') : '--'), 1)
      ]),
      peripheral_status: _withCtx(({ value }: { value?: number }) => [
        _createElementVNode("span", null, _toDisplayString(value === 1 ? _ctx.$t('usbPolicy.enabled') : _ctx.$t('usbPolicy.disabled')), 1)
      ]),
      ip_address: _withCtx(({ row }: { row?: any }) => [
        row
          ? (_openBlock(), _createBlock(IPColumn, {
              key: 0,
              cip: row.client_ip,
              sip: row.system_ip
            }, null, 8, ["cip", "sip"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})